import {
  useCallback,
  useEffect,
  useRef,
  useState,
  type CSSProperties,
  type JSX,
  type ReactNode,
} from 'react';
import { createPortal } from 'react-dom';
import { type ViewerTier } from './roles';
import { filterSections, type AdminSection } from './types';
import { prefersReducedMotion, isNarrowViewport } from './motion';

/**
 * The estate-standard Settings surface — one role-aware right slide-over, opened
 * from a gear button in every portal's header. Presentational: the portal passes
 * the resolved `viewer` tier + an ordered `sections` array; the drawer filters by
 * each section's `minRole`. Portals to <body> (escapes header stacking), traps
 * focus, closes on Escape / backdrop, locks body scroll, and honours
 * prefers-reduced-motion — the shell none of the four hand-rolled overlays had.
 * Self-styled with design tokens + fallbacks (like SignOutMenu), so it renders
 * in a Tailwind portal that never imports @cca/ui/components.
 */
export interface SettingsDrawerProps {
  open: boolean;
  onClose: () => void;
  viewer: ViewerTier;
  sections: AdminSection[];
  /** Drawer heading (default "Settings"). */
  title?: string;
  /** Portal brand accent — the one permitted per-portal colour (heading rule). */
  accent?: string;
  /** Optional pinned footer (e.g. an About/version line). */
  footer?: ReactNode;
  /** Desktop panel width in px (default 520). Mobile is always full-width. */
  width?: number;
  /** Deep-link: scroll to this section on open (`?settings=<key>`). */
  initialSectionKey?: string;
}

const DURATION = 180;

export function SettingsDrawer({
  open,
  onClose,
  viewer,
  sections,
  title = 'Settings',
  accent,
  footer,
  width = 520,
  initialSectionKey,
}: SettingsDrawerProps): JSX.Element | null {
  const [rendered, setRendered] = useState(open);
  const [shown, setShown] = useState(false);
  const panelRef = useRef<HTMLDivElement>(null);
  const reduce = prefersReducedMotion();
  const narrow = isNarrowViewport();

  // Mount/unmount with enter+exit transition, driven by the controlled `open`.
  useEffect(() => {
    if (open) {
      setRendered(true);
      const id = requestAnimationFrame(() => setShown(true));
      return () => cancelAnimationFrame(id);
    }
    setShown(false);
    const t = setTimeout(() => setRendered(false), reduce ? 0 : DURATION);
    return () => clearTimeout(t);
  }, [open, reduce]);

  // Body scroll-lock while mounted.
  useEffect(() => {
    if (!rendered || typeof document === 'undefined') return;
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = prev;
    };
  }, [rendered]);

  // Focus trap + Escape; restore focus to the opening trigger on close.
  useEffect(() => {
    if (!rendered || typeof document === 'undefined') return;
    const prevActive = document.activeElement as HTMLElement | null;
    const focusable = (): HTMLElement[] => {
      const root = panelRef.current;
      if (!root) return [];
      return Array.from(
        root.querySelectorAll<HTMLElement>(
          'button:not([disabled]), [href], input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])',
        ),
      );
    };
    const raf = requestAnimationFrame(() => {
      const f = focusable();
      (f[0] ?? panelRef.current)?.focus();
    });
    const onKey = (e: KeyboardEvent): void => {
      if (e.key === 'Escape') {
        onClose();
        return;
      }
      if (e.key !== 'Tab') return;
      const f = focusable();
      const first = f[0];
      const last = f[f.length - 1];
      if (!first || !last) return;
      const active = document.activeElement;
      if (e.shiftKey && active === first) {
        e.preventDefault();
        last.focus();
      } else if (!e.shiftKey && active === last) {
        e.preventDefault();
        first.focus();
      }
    };
    document.addEventListener('keydown', onKey);
    return () => {
      cancelAnimationFrame(raf);
      document.removeEventListener('keydown', onKey);
      prevActive?.focus?.();
    };
  }, [rendered, onClose]);

  // Deep-link: scroll to a section once shown.
  useEffect(() => {
    if (!shown || !initialSectionKey) return;
    const el = panelRef.current?.querySelector(`[data-section="${initialSectionKey}"]`);
    if (el) el.scrollIntoView({ block: 'start', behavior: reduce ? 'auto' : 'smooth' });
  }, [shown, initialSectionKey, reduce]);

  if (!rendered || typeof document === 'undefined') return null;

  const visible = filterSections(sections, viewer);
  const titleId = 'cca-settings-title';

  const backdrop: CSSProperties = {
    position: 'fixed',
    inset: 0,
    background: 'rgba(0,0,0,0.42)',
    opacity: shown ? 1 : 0,
    transition: reduce ? 'none' : `opacity ${DURATION}ms var(--ease-standard, ease)`,
    zIndex: 900,
  };
  const panel: CSSProperties = {
    position: 'fixed',
    top: 0,
    right: 0,
    bottom: 0,
    width: narrow ? '100vw' : `min(${width}px, 100vw)`,
    background: 'var(--color-panel, #ffffff)',
    boxShadow: 'var(--shadow-drawer, 0 10px 40px rgba(0,0,0,0.18))',
    transform: shown ? 'translateX(0)' : 'translateX(100%)',
    transition: reduce ? 'none' : `transform ${DURATION}ms var(--ease-standard, ease)`,
    zIndex: 1000,
    display: 'flex',
    flexDirection: 'column',
    outline: 'none',
  };
  const head: CSSProperties = {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    padding: '16px 20px',
    borderBottom: `2px solid ${accent ?? 'var(--color-structural, #333)'}`,
    flexShrink: 0,
  };
  const closeBtn: CSSProperties = {
    background: 'transparent',
    border: 0,
    fontSize: 20,
    lineHeight: 1,
    color: 'var(--color-muted, #6b7280)',
    cursor: 'pointer',
    padding: 4,
    borderRadius: 'var(--radius-xs, 4px)',
  };
  const bodyWrap: CSSProperties = { overflowY: 'auto', flex: 1, padding: '4px 20px 20px' };
  const sectionStyle: CSSProperties = {
    padding: '18px 0',
    borderBottom: '1px solid var(--color-border-soft, #f0efe9)',
  };
  const sectionHead: CSSProperties = {
    display: 'flex',
    alignItems: 'center',
    gap: 8,
    marginBottom: 10,
    color: 'var(--color-structural, #333)',
  };

  return createPortal(
    <>
      <div style={backdrop} onClick={onClose} aria-hidden="true" />
      <div
        ref={panelRef}
        role="dialog"
        aria-modal="true"
        aria-labelledby={titleId}
        tabIndex={-1}
        style={panel}
      >
        <div style={head}>
          <h2
            id={titleId}
            style={{ margin: 0, fontSize: 16, fontWeight: 700, color: 'var(--color-ink, #111827)' }}
          >
            {title}
          </h2>
          <button type="button" aria-label="Close settings" onClick={onClose} style={closeBtn}>
            ✕
          </button>
        </div>
        <div style={bodyWrap}>
          {visible.length === 0 ? (
            <p style={{ fontSize: 13, color: 'var(--color-muted, #6b7280)', padding: '18px 0' }}>
              Nothing to configure here.
            </p>
          ) : (
            visible.map((s) => (
              <section
                key={s.key}
                data-section={s.key}
                aria-labelledby={`cca-sec-${s.key}`}
                style={sectionStyle}
              >
                <div style={sectionHead}>
                  {s.icon}
                  <h3
                    id={`cca-sec-${s.key}`}
                    style={{
                      margin: 0,
                      fontSize: 13,
                      fontWeight: 700,
                      textTransform: 'uppercase',
                      letterSpacing: '0.04em',
                    }}
                  >
                    {s.title}
                  </h3>
                </div>
                <div>
                  {typeof s.body === 'function' ? s.body({ viewer, close: onClose }) : s.body}
                </div>
              </section>
            ))
          )}
          {footer && (
            <div
              style={{ padding: '16px 0 4px', fontSize: 12, color: 'var(--color-muted, #9ca3af)' }}
            >
              {footer}
            </div>
          )}
        </div>
      </div>
    </>,
    document.body,
  );
}

export interface UseSettingsDrawer {
  open: boolean;
  onOpen: () => void;
  onClose: () => void;
  /** Spread onto the header gear `.btn-header` button. */
  buttonProps: {
    onClick: () => void;
    'aria-haspopup': 'dialog';
    'aria-expanded': boolean;
  };
}

/** Owns the open/close state so the header button stays a one-liner (mirrors
 *  SignOutMenu's self-contained pattern). */
export function useSettingsDrawer(initial = false): UseSettingsDrawer {
  const [open, setOpen] = useState(initial);
  const onOpen = useCallback(() => setOpen(true), []);
  const onClose = useCallback(() => setOpen(false), []);
  return {
    open,
    onOpen,
    onClose,
    buttonProps: { onClick: onOpen, 'aria-haspopup': 'dialog', 'aria-expanded': open },
  };
}
